import {useCallback} from 'react';

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {Autocomplete, Libraries, LoadScriptNext} from '@react-google-maps/api';
import {useTranslation} from 'react-i18next';

import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
} from '@/app/shared-components';
import ConfirmAddressModal from '@/app/shared-components/ConfirmAddressModal/ConfirmAddressModal';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import SecureEncryptFooter from '@/app/shared-components/SecureEncryptFooter/SecureEncryptFooter';
import {
  allowedAutocompleteTypes,
  defaultAddressCountry,
} from '@/app/utils/AddressUtils/AddressUtils';
import {US_STATES} from '@/app/utils/AddressUtils/usStates';
import {ENV} from '@/constants/EnvConstants';
import {COLORS} from '@/styles/colors';

import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

import './styles';

const libraries: Libraries = ['places'];

const KycNotStartedContent = () => {
  const {
    addressFormErrors,
    formAddress,
    confirmAddress,
    setFormAddress,
    hasNoDetails,
    handlePlaceChanged,
    autoCompleteRef,
    disableButton,
    showModal,
    handleNavigateToPlaid,
    handleOnChangeCheckbox,
    handleSetValue,
    hasErrorAddressLine2,
    handleChangeAddressLine2,
    handleOnChangeState,
    handleOnClickContinue,
    handleOnClickEdit,
  } = useViewModelProvider();

  const {t} = useTranslation();

  const loadingGooglePlacesElement = (
    <div>{t('kyc.not_started.loading_google_places')}</div>
  );

  const renderUSApatriot = useCallback(
    () => (
      <div className="usa-patriot-container">
        <Typography className="usa-patriot">
          {t('kyc.not_started.usa_patriot')}
        </Typography>
      </div>
    ),
    [t],
  );

  return (
    <>
      <Box className="kyc-not-started-page-container">
        {/* Title Section */}
        <Box className="titles-container">
          <Box className="kyc-not-started-header">
            <Box className="kyc-not-started-content">
              <Typography
                component="span"
                className="kyc-not-started-title">
                {t('kyc.not_started.title_with_icon')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <LoadScriptNext
          loadingElement={loadingGooglePlacesElement}
          googleMapsApiKey={ENV.VITE_GOOGLE_PLACES_KEY}
          libraries={libraries}>
          <Box
            sx={{width: '100%'}}
            className="kyc-not-started-container">
            <Autocomplete
              onLoad={autocomplete => (autoCompleteRef.current = autocomplete)}
              options={{
                types: allowedAutocompleteTypes,
                componentRestrictions: {country: defaultAddressCountry},
              }}
              onPlaceChanged={handlePlaceChanged}
              className="kyc-not-started-autocomplete">
              <Box sx={{width: '100%'}}>
                <InputTextField
                  id="kyc-not-started-address-line-1"
                  label={t('kyc.not_started.form.address_line_1_label')}
                  placeholder={t('kyc.not_started.form.address_line_1_label')}
                  helperError={t('kyc.not_started.form.address_line_1_error')}
                  error={!!addressFormErrors.addressLine1}
                  value={
                    formAddress.addressLineShortened || formAddress.addressLine1
                  }
                  setValue={handleSetValue}
                  style={{
                    width: '100%',
                    fontSize: '16px',
                  }}
                />
              </Box>
            </Autocomplete>
          </Box>
        </LoadScriptNext>

        {formAddress.addressLine1 && (
          <>
            <Box
              sx={{
                rowGap: '20px',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}>
              <Box className="kyc-not-started-checkbox-container">
                <FormControlLabel
                  control={
                    <Checkbox
                      id="kyc-not-started-no-address-line-2-checkbox"
                      checked={hasNoDetails}
                      onChange={e => handleOnChangeCheckbox(e.target.checked)}
                      inputProps={{
                        'aria-label': t('kyc.not_started.form.checkbox_label'),
                      }}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 30,
                          width: 30,
                          height: 30,
                        },
                        '&.Mui-checked': {
                          color: COLORS.ON_SURFACE_1,
                        },
                        ...(hasErrorAddressLine2 && {
                          color: COLORS.LIGHT_ERROR,
                        }),
                      }}
                    />
                  }
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      textAlign: 'left',
                    },
                  }}
                  label={
                    <Typography
                      className={`kyc-not-started-checkbox-label ${hasErrorAddressLine2 ? 'kyc-not-started-checkbox-error-label' : ''}`}>
                      {t('kyc.not_started.form.checkbox_label')}
                    </Typography>
                  }
                />
              </Box>
              <InputTextField
                id="kyc-not-started-address-line-2"
                label={t('kyc.not_started.form.address_line_2_label')}
                placeholder={t('kyc.not_started.form.address_line_2_label')}
                maxLength={50}
                value={formAddress.addressLine2}
                setValue={handleChangeAddressLine2}
                style={{
                  width: '100%',
                  fontSize: '16px',
                }}
                error={hasErrorAddressLine2}
                disabled={hasNoDetails}
              />
            </Box>
            <InputTextField
              id="kyc-not-started-city"
              label={t('kyc.not_started.form.city')}
              placeholder={t('kyc.not_started.form.city')}
              value={formAddress.city}
              setValue={(value: string) => setFormAddress({city: value})}
              style={{
                width: '100%',
                fontSize: '16px',
              }}
              error={!!addressFormErrors.city}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 4,
              }}>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                    transform: 'translate(16px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      fontSize: '14px',
                      transform: 'translate(10px, -7px) scale(0.75)',
                      backgroundColor: 'white',
                      padding: '0 4px',
                      color: COLORS.ON_SURFACE_1,
                    },
                    '&.Mui-error': {
                      color: COLORS.RED_CINNABAR,
                    },
                  },

                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    height: '56px',
                    alignItems: 'center',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: !addressFormErrors.state
                        ? COLORS.ON_OUTLINE_LIGHT
                        : COLORS.RED_CINNABAR,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: !addressFormErrors.state
                        ? COLORS.ON_SURFACE_1
                        : COLORS.RED_CINNABAR,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: !addressFormErrors.state
                        ? COLORS.ON_SURFACE_1
                        : COLORS.RED_CINNABAR,
                    },
                  },
                }}>
                <InputLabel id="kyc-not-started-state-label">
                  {t('kyc.not_started.form.state')}
                </InputLabel>
                <Select
                  labelId="kyc-not-started-state-label"
                  id="kyc-not-started-state"
                  value={formAddress.region}
                  label={t('kyc.not_started.form.state')}
                  onChange={handleOnChangeState}
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '16px',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      color: COLORS.ON_SURFACE,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSelect-icon': {
                      color: COLORS.ON_SURFACE_1,
                    },
                  }}>
                  {US_STATES.map(state => (
                    <MenuItem
                      key={state.value}
                      value={state.value}
                      sx={{
                        fontSize: '14px',
                        fontWeight: '400',
                        color: COLORS.ON_SURFACE_VARIANT,
                        '&.Mui-selected': {
                          backgroundColor: COLORS.ON_SURFACE_4,
                        },
                      }}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <InputTextField
                id="kyc-not-started-zip-code"
                label={t('kyc.not_started.form.zip_code')}
                placeholder={t('kyc.not_started.form.zip_code')}
                value={formAddress.zipCode}
                setValue={(value: string) => setFormAddress({zipCode: value})}
                style={{
                  width: '100%',
                  fontSize: '16px',
                }}
                error={!!addressFormErrors.zipCode}
              />
            </Box>
          </>
        )}

        <SecureEncryptFooter />

        <ButtonContainer sticky>
          {renderUSApatriot()}
          <AppButton
            variant={AppButtonVariant.onPrimary}
            id="kyc-not-started-continue-button"
            onClick={handleOnClickContinue}
            disabled={disableButton}
            label={t('kyc.not_started.form.button')}
          />
        </ButtonContainer>
      </Box>
      <ConfirmAddressModal
        open={showModal}
        onClose={handleOnClickEdit}
        onContinue={handleNavigateToPlaid}
        confirmAddress={confirmAddress}
      />
    </>
  );
};

const KycNotStartedPage = () => (
  <ViewModelProvider>
    <KycNotStartedContent />
  </ViewModelProvider>
);

export default KycNotStartedPage;
